/**
 * Block palette + factory + inspector registry. Centralising the per-type
 * concerns here keeps the editor component slim and makes adding a new
 * block type a one-file change.
 */
import type { KitePageBlock, KitePageBlockType, KiteFormField } from '@xsc/shared';
import { generateId } from '@xsc/shared';

export interface PaletteEntry {
  type: KitePageBlockType;
  label: string;
  icon: string;
  description: string;
}

export const BLOCK_PALETTE: PaletteEntry[] = [
  { type: 'hero',   label: 'Hero',    icon: '🏁', description: 'Headline banner with optional background image' },
  { type: 'text',   label: 'Text',    icon: '📝', description: 'A paragraph of plain text' },
  { type: 'image',  label: 'Image',   icon: '🖼️', description: 'A single image with optional caption' },
  { type: 'button', label: 'Button',  icon: '🔘', description: 'A call-to-action link' },
  { type: 'form',   label: 'Form',    icon: '📋', description: 'Collect submissions (RSVP, contact, etc.)' },
  { type: 'video',  label: 'Video',   icon: '▶️',  description: 'Embed a YouTube, Vimeo, or MP4 video' },
  { type: 'faq',    label: 'FAQ',     icon: '❓', description: 'Expandable question/answer list' },
  { type: 'spacer', label: 'Spacer',  icon: '↕️',  description: 'Vertical spacing between blocks' },
  { type: 'footer', label: 'Footer',  icon: '⚓', description: 'Footer text + unsubscribe link' },
];

export function newBlock(type: KitePageBlockType): KitePageBlock {
  const id = generateId('kpg').replace('kpg_', 'b_');
  switch (type) {
    case 'hero':
      return { id, type, props: { headline: 'Your headline here', subheadline: 'A short tagline', background_image_url: '' } };
    case 'text':
      return { id, type, props: { body: 'Write your message here. Use {{first_name}} to insert variables.' } };
    case 'image':
      return { id, type, props: { url: '', alt: '', width_pct: 100 } };
    case 'button':
      return { id, type, props: { label: 'Click me', href: '#', variant: 'primary' } };
    case 'form':
      return {
        id,
        type,
        props: {
          fields: [
            { name: 'name', label: 'Your name', type: 'text', required: true },
            { name: 'email', label: 'Email', type: 'email', required: true },
          ] as KiteFormField[],
          submit_label: 'Submit',
          event_name: 'kite.form.submitted',
          success_message: 'Thanks! Your submission was received.',
          redirect_url: '',
        },
      };
    case 'video':
      return { id, type, props: { url: '', caption: '' } };
    case 'faq':
      return {
        id,
        type,
        props: {
          items: [
            { question: 'What is this?', answer: 'Edit me in the inspector.' },
          ],
        },
      };
    case 'spacer':
      return { id, type, props: { height_px: 32 } };
    case 'footer':
      return {
        id,
        type,
        props: { text: '© Your company', unsubscribe_url: '' },
      };
  }
}
